import { compileStyles } from 'lib/styled-helpers'
import React, { FC, InputHTMLAttributes } from 'react'
import styled from 'styled-components'

import { getProjectName } from '../../lib/get-project-name'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  value?: string
  block?: boolean
  type?: string
  label?: string
  error?: string
  onEnter?: () => void
  inputRef: any
  isFocused?: boolean
  setIsFocused?: (val: boolean) => void
}

export const Input: FC<InputProps> = ({
  type = 'text',
  label,
  value,
  onChange,
  error,
  onFocus,
  onBlur,
  onEnter,
  block = false,
  inputRef,
  isFocused = false,
  setIsFocused,
  required,
  ...restProps
}) => {
  return (
    <WrapperOut>
      <Wrapper block={block} onClick={() => inputRef?.current?.focus()}>
        <StyledInput
          type={type}
          value={value}
          ref={inputRef}
          onFocus={(e) => {
            setIsFocused && setIsFocused(true)
            onFocus && onFocus(e)
          }}
          onBlur={(e) => {
            setIsFocused && setIsFocused(false)
            onBlur && onBlur(e)
          }}
          {...restProps}
        />
        <Label>
          {label}
          {required && '*'}
        </Label>
        {getProjectName() !== 'avito' && isFocused && <BottomBorder visible={isFocused} error={error} />}
      </Wrapper>
    </WrapperOut>
  )
}

const WrapperOut = styled.div`
  width: 100%;
`

const Wrapper = styled.div<{ block: boolean }>`
  position: relative;
  width: ${({ block, theme }) => (block ? '100%' : theme.spacing[72])};
  height: ${({ theme }) => theme.spacing[16]};
`

const Label = styled.label`
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding-left: ${({ theme }) => theme.spacing[4]};
  pointer-events: none;
  box-sizing: border-box;
  transition: transform 0.25s, font-size 0.25s;
  color: ${({ theme }) => theme.palette.grey[500]};
  ${({ theme }) => theme.textStyles.p3};
  font-weight: 300;
  transform: ${({ theme }) => `translateY(-${theme.spacing[4]})`};
`

const BottomBorder = styled.div<{ visible: boolean; error?: string }>`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 3px;
  background: ${({ theme, error }) => (error ? theme.palette.alert : theme.palette.brand.primary)};
  transform: scaleY(${({ visible }) => (visible ? 1 : 0)});
  transform-origin: 50% 100% 0;
  transition: transform 0.25s;
`

const StyledInput = styled.input`
  position: relative;
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => `${theme.spacing[7]} ${theme.spacing[5]} ${theme.spacing[2]}`};
  outline: none;
  box-sizing: border-box;

  &:hover ~ ${BottomBorder} {
    transform: scaleY(1);
  }

  ${({ theme }) => `
    border: 1px solid ${theme.palette.grey[200]}};
    color: ${theme.palette.grey[900]};
    ${compileStyles(theme.textStyles.h6)};
  `}

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  font-weight: 300;
  border-radius: ${() => getProjectName() === 'avito' && '0 16px 16px 0'};
`
